import { Persistor } from 'redux-persist'
import { REDUX_PERSIST_CONSTANTS } from '../constants'
import { ApiValidationError } from '../../types'

// Function to add or update parameters in the URL
export function addParamToURL(paramName: string, paramValue: string): void {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  params.set(paramName, paramValue)
  url.search = params.toString()
  window.history.pushState({ path: url.href }, '', url.href)
}

// Function to get the value of a parameter from the URL
export function getParamFromURL(paramName: string): string | null {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  return params.get(paramName)
}

export const preserveTableColumn = (persist: Persistor) => {
  const root = localStorage.getItem(`persist:${REDUX_PERSIST_CONSTANTS.ROOT}`)

  if (!root) return

  try {
    const parsed = JSON.parse(root)

    const onlyTableColumn = {
      tableColumn: parsed.tableColumn || '{}',
    }
    persist.purge().then(() => {
      localStorage.setItem(
        `${REDUX_PERSIST_CONSTANTS.PERSIST}:${REDUX_PERSIST_CONSTANTS.ROOT}`,
        JSON.stringify(onlyTableColumn)
      )
    })
  } catch (err) {
    console.error('Error preserving tableColumn:', err)
  }
}

type ValidParamValue = boolean | string | number | null | undefined

export const parseNumericId = (
  value: string | number | null | undefined
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = typeof value === 'number' ? value : Number(value)

  return Number.isFinite(parsed) ? parsed : null
}

export const isValidEntityDataId = (
  value: string | number | null | undefined
): boolean => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  return value.trim().length > 0
}

export const isValidLayout = <T extends { type?: string }>(
  layout: T | null | undefined
): layout is T & { type: string } =>
  layout != null && typeof layout.type === 'string'

export const parseCommunicationIdFromSearch = (
  search: string
): number | null => {
  const raw = search.replace(/^\?/, '')
  if (!raw) {
    return null
  }

  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`
  )
  const named = params.get('communicationId')
  if (named) {
    return parseNumericId(named)
  }

  if (!raw.includes('=')) {
    return parseNumericId(raw)
  }

  for (const [key, value] of params.entries()) {
    if (key && !value && /^\d+$/.test(key)) {
      return parseNumericId(key)
    }
  }

  return null
}

export const validateApiParams = <T extends Record<string, ValidParamValue>>(
  params: T,
  options: {
    endpoint: string
    method: string
  }
): void => {
  const invalidFields: string[] = []

  const isInvalid = (value: ValidParamValue): boolean =>
    value === undefined ||
    value === null ||
    value === '' ||
    (typeof value === 'string' && !value.trim()) ||
    (typeof value === 'number' && Number.isNaN(value))

  for (const [field, value] of Object.entries(params)) {
    if (isInvalid(value)) {
      invalidFields.push(field)
    }
  }

  if (invalidFields.length > 0) {
    const err = new Error(
      `Invalid API endpoint: ${options.endpoint}`
    ) as ApiValidationError

    err.name = 'ApiValidationError'

    throw err
  }
}

export const buildProgramServiceHeaders = (
  programId?: string,
  serviceId?: string
): Record<string, string> => ({
  ...(programId ? { 'X-Program-Id': programId } : {}),
  ...(serviceId ? { 'X-Service-Id': serviceId } : {}),
})
